import { useState, useEffect } from 'react';
import { useToast } from '@/components/ui/use-toast';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Bell, BellOff, Loader2, Save } from 'lucide-react';
import { supabase } from '@/lib/supabase';

export interface NotificationPreferences {
  weekly_review: boolean;
  new_convergences: boolean;
  autopost_content_ready: boolean;
  autopost_publish_success: boolean;
  autopost_publish_failure: boolean;
  subscription_changes: boolean;
}

interface NotificationPreferencesProps {
  brandId: string;
  showQuickActions?: boolean;
  onSave?: () => void;
  className?: string;
}

export function NotificationPreferences({ 
  brandId, 
  showQuickActions = true,
  onSave,
  className = ''
}: NotificationPreferencesProps) {
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    weekly_review: false,
    new_convergences: false,
    autopost_content_ready: false,
    autopost_publish_success: false,
    autopost_publish_failure: false,
    subscription_changes: false,
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const { toast } = useToast();

  // Fetch current preferences
  useEffect(() => {
    fetchPreferences();
  }, [brandId]);

  const fetchPreferences = async () => {
    try {
      setLoading(true);
      const session = await supabase.auth.getSession();
      if (!session.data.session) return;

      const response = await fetch(
        `${import.meta.env.VITE_WORKERS_URL}/rainfactory/brands/${brandId}/notification-preferences`,
        {
          headers: {
            Authorization: `Bearer ${session.data.session.access_token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch notification preferences');
      }

      const data = await response.json();
      if (data.preferences) {
        setPreferences(data.preferences);
      }
    } catch (error) {
      console.error('Error fetching notification preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to load notification preferences',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = (key: keyof NotificationPreferences) => {
    setPreferences((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
    setHasChanges(true);
  };

  const savePreferences = async () => {
    try {
      setSaving(true);
      const session = await supabase.auth.getSession();
      if (!session.data.session) return;

      const response = await fetch(
        `${import.meta.env.VITE_WORKERS_URL}/rainfactory/brands/${brandId}/notification-preferences`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${session.data.session.access_token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ preferences }),
        }
      );

      if (!response.ok) {
        throw new Error('Failed to save notification preferences');
      }

      toast({
        title: 'Success',
        description: 'Notification preferences saved successfully',
      });
      setHasChanges(false);
      onSave?.();
    } catch (error) {
      console.error('Error saving notification preferences:', error);
      toast({
        title: 'Error',
        description: 'Failed to save notification preferences',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={`flex items-center justify-center py-6 ${className}`}>
        <Loader2 className="h-6 w-6 animate-spin text-indigo-500" />
      </div>
    );
  }

  const allEnabled = Object.values(preferences).every((v) => v);
  const noneEnabled = Object.values(preferences).every((v) => !v);

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Save button at the top if there are changes */}
      {hasChanges && (
        <div className="flex justify-end">
          <Button onClick={savePreferences} disabled={saving} size="sm">
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Save className="mr-2 h-4 w-4" />
            )}
            Save Changes
          </Button>
        </div>
      )}

      {/* Quick actions */}
      {showQuickActions && (
        <div className="flex gap-2 flex-wrap">
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPreferences({
                weekly_review: true,
                new_convergences: true,
                autopost_content_ready: true,
                autopost_publish_success: true,
                autopost_publish_failure: true,
                subscription_changes: true,
              });
              setHasChanges(true);
            }}
            disabled={allEnabled}
            className="flex-1 sm:flex-none"
          >
            <Bell className="mr-2 h-4 w-4" />
            Enable All
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setPreferences({
                weekly_review: false,
                new_convergences: false,
                autopost_content_ready: false,
                autopost_publish_success: false,
                autopost_publish_failure: false,
                subscription_changes: false,
              });
              setHasChanges(true);
            }}
            disabled={noneEnabled}
            className="flex-1 sm:flex-none"
          >
            <BellOff className="mr-2 h-4 w-4" />
            Disable All
          </Button>
        </div>
      )}

      {/* Individual preferences */}
      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="weekly_review">Weekly Review</Label>
            <p className="text-sm text-muted-foreground">
              Receive weekly summaries of brand activity
            </p>
          </div>
          <Switch
            id="weekly_review"
            checked={preferences.weekly_review}
            onCheckedChange={() => handleToggle('weekly_review')}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="new_convergences">New Convergences</Label>
            <p className="text-sm text-muted-foreground">
              Get notified about new content opportunities
            </p>
          </div>
          <Switch
            id="new_convergences"
            checked={preferences.new_convergences}
            onCheckedChange={() => handleToggle('new_convergences')}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="autopost_content_ready">Content Ready</Label>
            <p className="text-sm text-muted-foreground">
              Notification when autopost content is ready for review
            </p>
          </div>
          <Switch
            id="autopost_content_ready"
            checked={preferences.autopost_content_ready}
            onCheckedChange={() => handleToggle('autopost_content_ready')}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="autopost_publish_success">Publishing Success</Label>
            <p className="text-sm text-muted-foreground">
              Confirmation when content is successfully published
            </p>
          </div>
          <Switch
            id="autopost_publish_success"
            checked={preferences.autopost_publish_success}
            onCheckedChange={() => handleToggle('autopost_publish_success')}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="autopost_publish_failure">Publishing Failures</Label>
            <p className="text-sm text-muted-foreground">
              Alerts when content publishing fails
            </p>
          </div>
          <Switch
            id="autopost_publish_failure"
            checked={preferences.autopost_publish_failure}
            onCheckedChange={() => handleToggle('autopost_publish_failure')}
          />
        </div>

        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="subscription_changes">Subscription Changes</Label>
            <p className="text-sm text-muted-foreground">
              Updates about billing and subscription status
            </p>
          </div>
          <Switch
            id="subscription_changes"
            checked={preferences.subscription_changes}
            onCheckedChange={() => handleToggle('subscription_changes')}
          />
        </div>
      </div>
    </div>
  );
}